/*
 * rheincool — kW-Rechner (kW-Rechner-Handoff.md)
 * kW = roomSize × W/m² × Decke × Sonne × Dämmung, auf Gerätestufen gerundet.
 * Alle Faktoren kommen aus der Section-Konfiguration ([PLATZHALTER —
 * fachlich validieren]), zentrale Konstanten aus #rc-constants.
 * Ergebnis wird als Store-Key 'kw' publiziert (article-store.js).
 *
 * CTA-Ziel: Setting cta_url_base + ?leistung=<Alias>. Der Alias wird auf der
 * Kategorieseite von filter-alias.js gegen die S&D-Kühlleistungsgruppen
 * aufgelöst (snippets/filter-alias-config.liquid) — hier steht bewusst keine
 * Filter-ID, damit eine Umgruppierung in S&D nur eine Pflegestelle hat.
 *
 * Ergebnis-Zeile (19.09.2026): Die Section „Ergebnis-Produkte" mit store_key
 * kw zeigte „Ihr Ergebnis: 5,0 kW – diese Geräte passen", ihr Knopf „Alle
 * passenden Geräte ansehen" führte aber fest auf die ungefilterte Kategorie.
 * Enthält ihre URL ?leistung= (Opt-in im Theme-Editor), folgt sie jetzt dem
 * Rechner. Andere Ziele dort (Beratung, Seite) bleiben unangetastet.
 */
(() => {
  'use strict';
  if (customElements.get('rc-kw-calculator')) return;

  /* Gerätestufen laut Handoff — kleinste Stufe, die den Bedarf deckt */
  const TIERS = [2.0, 2.5, 3.5, 5.0, 6.0];
  const LEISTUNG_ALIAS = {
    '2.0': '2-0-kw',
    '2.5': '2-5-kw',
    '3.5': '3-5-kw',
    '5.0': '5-0-kw',
  };
  const RESULT_ROW_CTA =
    'rc-result-products[data-store-key="kw"] a[data-rc-track-quelle="result-products"][href*="leistung="]';

  class RcKwCalculator extends window.RcCalculator {
    init() {
      this.storeKey = 'kw';
      this.trackPrefix = 'calculator';
      this.lastSignature = null;

      this.els = {
        number: this.querySelector('[data-kw-number]'),
        unit: this.querySelector('[data-kw-unit]'),
        explain: this.querySelector('[data-kw-explain]'),
        roomLabel: this.querySelector('[data-calc-label-value="room"]'),
        stickyValue: this.querySelector('[data-kw-sticky-value]'),
        ctas: Array.from(this.querySelectorAll('[data-kw-cta]')),
        resultRowCtas: Array.from(document.querySelectorAll(RESULT_ROW_CTA)),
      };

      this.els.ctas.forEach((cta) => {
        cta.addEventListener('click', () => {
          const result = window.rcStore?.get('kw');
          this.track('calculator_cta_click', {
            kw: result ? result.kw : null,
            multisplit: result ? result.multisplit : false,
          });
        });
      });

      /* Schon vor der ersten Eingabe auf das Ergebnis der Startwerte zeigen
         (25 m² → 2,5 kW), statt auf die ungefilterte Kategorie — der Knopf
         der Ergebnis-Zeile ist sichtbar, bevor jemand rechnet. */
      const initial = this.compute();
      if (initial) this.setHrefs(this.ctaHref(initial));
    }

    setHrefs(href) {
      // Ohne Ziel (Setting leer, Konfiguration unlesbar) die vorhandene
      // href stehen lassen statt „undefined" als Link zu setzen.
      if (!href) return;
      this.els.ctas.forEach((cta) => {
        cta.href = href;
      });
      this.els.resultRowCtas.forEach((cta) => {
        cta.href = href;
      });
    }

    fill(template, values) {
      return Object.keys(values).reduce(
        (text, key) => text.split(`[${key}]`).join(values[key]),
        template || ''
      );
    }

    compute() {
      const room = this.fieldValue('room');
      if (Number.isNaN(room) || room <= 0) return null;
      const c = this.config;

      const ceilingKey = this.segmentValue('ceiling') || 'normal';
      const sunKey = this.segmentValue('sun') || 'normal';
      const insulationKey = this.segmentValue('insulation') || 'altbau';

      const ceiling = ceilingKey === 'high' ? c.factorCeiling : 1;
      const sun =
        { wenig: c.factorSunLow, normal: c.factorSunNormal, dg_sued: c.factorSunHigh }[sunKey] || 1;
      const insulation = insulationKey === 'neubau' ? c.factorInsNew : c.factorInsOld;

      const kwNeed = (room * c.wpm2 * ceiling * sun * insulation) / 1000;
      const kw = TIERS.find((tier) => tier >= kwNeed) || TIERS[TIERS.length - 1];
      const multisplit = room > c.multisplitThreshold || !LEISTUNG_ALIAS[kw.toFixed(1)];

      return {
        kw,
        multisplit,
        roomSize: room,
        ceiling: ceilingKey,
        sun: sunKey,
        insulation: insulationKey,
      };
    }

    ctaHref(result) {
      if (result.multisplit) return this.config.multisplitUrl;
      const base = this.config.ctaUrlBase || '';
      const separator = base.indexOf('?') === -1 ? '?' : '&';
      return `${base}${separator}leistung=${LEISTUNG_ALIAS[result.kw.toFixed(1)]}`;
    }

    renderResult(result) {
      const s = this.config.strings || {};
      const kwText = this.formatNumber(result.kw, 1);
      const roomText = this.formatNumber(result.roomSize, 0);
      const href = this.ctaHref(result);

      if (this.els.roomLabel) this.els.roomLabel.textContent = `${roomText} m²`;

      if (result.multisplit) {
        if (this.els.number) this.els.number.textContent = s.multisplitLabel;
        if (this.els.unit) this.els.unit.hidden = true;
        if (this.els.explain) this.els.explain.textContent = this.fill(s.multisplitExplain, { room: roomText });
        if (this.els.stickyValue) this.els.stickyValue.textContent = s.multisplitLabel;
        this.announce(this.fill(s.multisplitAnnounce, { room: roomText }));
      } else {
        if (this.els.number) this.els.number.textContent = kwText;
        if (this.els.unit) this.els.unit.hidden = false;
        if (this.els.explain) this.els.explain.textContent = this.fill(s.explain, { kw: kwText, room: roomText });
        if (this.els.stickyValue) this.els.stickyValue.textContent = `${kwText} kW`;
        this.announce(this.fill(s.announce, { kw: kwText, room: roomText }));
      }

      this.setHrefs(href);
      this.els.ctas.forEach((cta) => {
        if (!cta.hasAttribute('data-kw-sticky-cta')) {
          cta.textContent = result.multisplit ? s.multisplitCta : this.fill(s.cta, { kw: kwText });
        } else if (result.multisplit) {
          cta.textContent = s.multisplitCta;
        } else {
          cta.textContent = s.ctaShort;
        }
      });

      const signature = result.multisplit ? 'multisplit' : String(result.kw);
      if (signature !== this.lastSignature) {
        this.lastSignature = signature;
        this.track('calculator_result', {
          kw: result.kw,
          roomSize: result.roomSize,
          ceiling: result.ceiling,
          sun: result.sun,
          insulation: result.insulation,
          multisplit: result.multisplit,
        });
      }
    }

    trackParams() {
      return { roomSize: this.fieldValue('room') };
    }
  }

  customElements.define('rc-kw-calculator', RcKwCalculator);
})();
